import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { navigate } from 'gatsby';
import { Box, Button, Backdrop, HorizontalStack } from '@houzz/ui';
import Autocomplete from '@houzz/ui-autocomplete';
import MagnifyingGlassIcon from '@houzz/icons/MagnifyingGlass';

const SEARCHABLE_GROUPS = ['Components', 'Foundations'];

function getSearchableLinks(nodes = []) {
    return nodes
        .filter(({ node: { frontmatter } }) => SEARCHABLE_GROUPS.includes(frontmatter?.group))
        .map((link) => {
            const {
                node: { frontmatter },
            } = link;
            return { ...frontmatter };
        });
}

function getGroupedOptions(links = []) {
    const groups = {};

    links.forEach(({ group, title, path }) => {
        if (!groups[group]) {
            groups[group] = {
                label: group,
                options: [],
            };
        }

        groups[group].options.push({ label: title, value: path });
    });

    Object.values(groups).forEach((group) => {
        group.options.sort((a, b) => a.label.localeCompare(b.label));
    });

    return Object.values(groups);
}

const StyledAutocomplete = styled(Autocomplete)`
    [role='combobox'] {
        background: transparent;
    }
`;

const Search = ({ edges }) => {
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const openSearch = () => setIsSearchOpen(true);
    const closeSearch = () => setIsSearchOpen(false);
    const handleKeydown = (event) => {
        const { key } = event;

        switch (key) {
            case 'k':
                if (event.metaKey && !isSearchOpen) {
                    openSearch();
                }
                break;
            case 'Escape':
                closeSearch();
                break;
            default:
                break;
        }
    };
    const handleNavigation = ({ value: path }) => {
        closeSearch();
        navigate(path);
    };
    const options = useMemo(() => getGroupedOptions(getSearchableLinks(edges)), [edges]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeydown);
        return () => window.removeEventListener('keydown', handleKeydown);
    }, []);

    return (
        <>
            <Button variant="outline" colorVariant="neutral" icon={<MagnifyingGlassIcon />} onClick={openSearch}>
                Search
                <HorizontalStack as="span" ml="S" gap="XXS">
                    <kbd>⌘</kbd>
                    <kbd>K</kbd>
                </HorizontalStack>
            </Button>
            {isSearchOpen && (
                <>
                    <Backdrop bg="overlayLight" />
                    <Box position="fixed" right={30} zIndex="modal">
                        <StyledAutocomplete
                            width={290}
                            size="large"
                            inputProps={{ autoFocus: true }}
                            placeholder="Search docs…"
                            options={options}
                            elementBefore={<MagnifyingGlassIcon />}
                            noOptionsLabel="No results"
                            onChange={handleNavigation}
                            onBlur={() => setTimeout(closeSearch, 100)}
                        />
                    </Box>
                </>
            )}
        </>
    );
};

export default Search;
